import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { OptionalObjectSchema } from 'yup/lib/object';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { AutocompleteOption } from '@/Components/shadcn';
import {
    maxYearsOfExperience,
    minLocationRadius,
    minYearsOfExperience,
    useIdentifiedListContext,
} from '../utils/IdentifiedListContext';
import {
    RefineSearchFilters,
    LanguageProficiency,
    LanguageProficiencyLevelList,
    SearchPeriod,
    SearchPeriodList,
} from '../../../api/useSearchFilters';
import YupValidationErrors from '@/Utils/YupValidationErrors';
import { SearchCriteria } from '@/Models/SearchCriteria';
import { GeocodeResponse } from '../../../api/DTO/GeocodeResponse';

export const oneFilterMinimumRule = 'one-filter-minimum-rule';
export const fillEveryRankingCriteriaRule = 'fill-every-ranking-criteria-rule';

interface RHFCriteriaValue extends SearchCriteria {}

/** Form-stored chip rows only use `label` (not `group`, which is dropdown-only). */
const filtersAutocompleteField: OptionalObjectSchema<
    Record<keyof Pick<AutocompleteOption, 'label'>, yup.StringSchema>
> = yup.object({
    label: yup.string().required(),
});

export const identifiedFiltersFormValidator = yup
    .object({
        jobTitles: yup.array().of(filtersAutocompleteField).optional(),
        jobTitleSearchPeriod: yup.mixed<SearchPeriod>().oneOf(SearchPeriodList).required(),
        companies: yup.array().of(filtersAutocompleteField).optional(),
        companiesSearchPeriod: yup.mixed<SearchPeriod>().oneOf(SearchPeriodList).required(),
        locations: yup.array().of(yup.object<Record<keyof GeocodeResponse, yup.StringSchema>>()).optional(),
        locationRadius: yup.number().default(minLocationRadius).min(minLocationRadius),
        keywords: yup.array().of(filtersAutocompleteField).optional(),
        yearsOfExperience: yup
            .array()
            .of(yup.number().required().min(minYearsOfExperience).max(maxYearsOfExperience))
            .length(2)
            .optional(),
        rankingCriteria: yup
            .array()
            .of(
                // this can be simplified with yup.ObjectSchema or even inferred, but does not work maybe because yup is outdated
                yup.object<Record<keyof RHFCriteriaValue, yup.StringSchema>>({
                    id: yup.string().defined(),
                    criterion: yup
                        .string()
                        .required(YupValidationErrors.Required)
                        .max(1024, YupValidationErrors.MaxSymbolsValidationError),
                }),
            )
            .min(1)
            .max(5)
            .required(),
        includeEmptyExperience: yup.boolean().default(false),
        languages: yup.array().of(filtersAutocompleteField).optional(),
        languageProficiency: yup.mixed<LanguageProficiency>().oneOf(LanguageProficiencyLevelList).required(),
    })
    .defined()
    .test(fillEveryRankingCriteriaRule, YupValidationErrors.AISearchFillEveryRankingCriteriaRequired, (value) => {
        const { rankingCriteria } = value as RefineSearchFilters;
        const isValid = rankingCriteria.find((x) => x.criterion.length === 0) === undefined;

        return isValid;
    });

export const useRefineSearchForm = () => {
    const { refineSearchFilters } = useIdentifiedListContext();
    const [disabled, setDisabled] = useState(false);
    const form = useForm<RefineSearchFilters>({
        mode: 'all',
        disabled,
        defaultValues: refineSearchFilters,
        resolver: yupResolver(identifiedFiltersFormValidator),
    });

    useEffect(() => {
        form.reset(refineSearchFilters);
    }, [refineSearchFilters]);

    useEffect(() => setDisabled(form.formState.isSubmitting), [form.formState.isSubmitting]);

    return form;
};

export const useRefineSearchFormContext = () => useFormContext<RefineSearchFilters>();
